import React, { useEffect, useState } from "react";
import config from "../../config";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TextField,
  Box,
  CircularProgress,
  Button,
  Chip,
  Tooltip,
} from "@mui/material";
import { motion } from "framer-motion";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import * as XLSX from "xlsx";
import { useRoomNames } from './useRoomNames';
import { toast } from 'react-toastify';
  const company = JSON.parse(sessionStorage.getItem("companyDetails"));
export default function HoldMaintenanceReport() {
  const { roomLabel } = useRoomNames();
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchRoom, setSearchRoom] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [freeLoading, setFreeLoading] = useState({});

  const token = sessionStorage.getItem("token");

  const fetchRooms = async () => {
    setLoading(true);
    try {
      const res = await fetch(
        `${config.API_BASE_URL}/Home/Getholdandmaintancerooms/${encodeURIComponent(company?.id)}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const data = await res.json();
      setRooms(data || []);
    } catch (err) {
      console.error("Error fetching hold/maintenance rooms", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRooms();
  }, []);

  // Date range + room number filter
  const filteredRooms = rooms.filter((r) => {
    const matchesSearch = r.roomNumber
      ?.toLowerCase()
      .includes(searchRoom.toLowerCase());

    let matchesDate = true;
    if (startDate || endDate) {
      const fromDate = new Date(r.fromDate);
      if (startDate) matchesDate = matchesDate && fromDate >= new Date(startDate);
      if (endDate) matchesDate = matchesDate && fromDate <= new Date(endDate);
    }

    return matchesSearch && matchesDate;
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const exportToExcel = () => {
    if (filteredRooms.length === 0) {
      toast.info("No data to export");
      return;
    }

    const formatted = filteredRooms.map((r, i) => ({
      "#": i + 1,
      "Room No": r.roomNumber,
      Type: r.statusType,
      "From Date": formatDate(r.fromDate),
      "To Date": formatDate(r.toDate),
      Name: r.holdName || "-",
      Mobile: r.holdMobile || "-",
      City: r.holdCity || "-",
      Remark: r.maintenanceRemark || "-",
    }));

    const ws = XLSX.utils.json_to_sheet(formatted);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "HoldMaintenance");
    XLSX.writeFile(wb, "Hold_Maintenance_Rooms.xlsx");
  };

  const handleFreeRoom = async (id) => {
    if (!window.confirm("Are you sure you want to release/free this room?")) return;

    setFreeLoading((prev) => ({ ...prev, [id]: true }));

    try {
      const res = await fetch(
        `${config.API_BASE_URL}/Home/ReleaseHoldOrMaintenance/${id}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!res.ok) throw new Error("Release failed");
      toast.success("Room released successfully!");
      await fetchRooms();
    } catch (err) {
      toast.error("Failed to release room");
      console.error(err);
    } finally {
      setFreeLoading((prev) => ({ ...prev, [id]: false }));
    }
  };

  const clearDateFilters = () => {
    setStartDate("");
    setEndDate("");
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" minHeight="60vh">
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 4 }}>
      <Typography
        variant="h4"
        fontWeight="bold"
        mb={3}
        textAlign="center"
        sx={{ color: "#0a2540" }}
      >
        🔒 Hold & Maintenance Rooms
      </Typography>

      {/* Filters */}
      <Box display="flex" gap={2} mb={3} flexWrap="wrap" alignItems="center">
        <TextField
          label="🔍 Search by Room Number"
          size="small"
          value={searchRoom}
          onChange={(e) => setSearchRoom(e.target.value)}
          sx={{ minWidth: 220 }}
        />

        <TextField
          label="From Date"
          type="date"
          size="small"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />

        <TextField
          label="To Date"
          type="date"
          size="small"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />

        <Button variant="outlined" color="secondary" onClick={clearDateFilters}>
          Clear Dates
        </Button>

        <Button
          variant="outlined"
          startIcon={<FileDownloadIcon />}
          onClick={exportToExcel}
          sx={{ borderRadius: 2, fontWeight: "bold" }}
        >
          Export to Excel
        </Button>
      </Box>

      {/* Table with borders */}
      <Paper sx={{ borderRadius: 2, overflow: "hidden", border: "1px solid #e0e0e0" }}>
        <TableContainer sx={{ maxHeight: 580 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                {["Room No", "Type", "From", "To", "Name", "Mobile", "City", "Remark", "Action"].map((header) => (
                  <TableCell
                    key={header}
                    sx={{
                      backgroundColor: "#0a2540",
                      color: "#fff",
                      fontWeight: "bold",
                      border: "1px solid #444",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {header}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {filteredRooms.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 5, border: "1px solid #ddd" }}>
                    No records found
                  </TableCell>
                </TableRow>
              ) : (
                filteredRooms.map((r) => (
                  <TableRow
                    key={r.id}
                    hover
                    sx={{
                      "&:hover": { backgroundColor: "#f5faff" },
                      "& td": {
                        border: "1px solid #e0e0e0",
                        py: 1.5,
                      },
                    }}
                  >
                    <TableCell sx={{ fontWeight: "medium" }}>{roomLabel(r.roomNumber)}</TableCell>
                    <TableCell>
                      <Chip
                        label={r.statusType}
                        // color={r.statusType === "Hold" ? "warning" : "error"}
                        // size="small"
                      />
                    </TableCell>
                    <TableCell>{formatDate(r.fromDate)}</TableCell>
                    <TableCell>{formatDate(r.toDate)}</TableCell>
                    <TableCell>{r.holdName || "-"}</TableCell>
                    <TableCell>{r.holdMobile || "-"}</TableCell>
                    <TableCell>{r.holdCity || "-"}</TableCell>
                    <TableCell>{r.maintenanceRemark || "-"}</TableCell>
<TableCell>
  {(() => {
    if (r.statusType === "FREE") return null;
    if (!r.isActive) return null;

    return (
      <Tooltip title="Release / Free this room">
        <Button
          variant="outlined"
          color="error"
          size="small"
          startIcon={<LockOpenIcon />}
          onClick={() => handleFreeRoom(r.id)}
          disabled={freeLoading[r.id]}
        >
          {freeLoading[r.id] ? "Releasing..." : "Free"}
        </Button>
      </Tooltip>
    );
  })()}
</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}